import {
  Component,
  Input,
  ElementRef,
  EventEmitter,
  Output,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import {
  RevenueChartHelperService,
  HighchartsConfiguration,
} from '@/app/core/chart/base';
import { FormatService } from '@/app/core/query';
import { HistogramLabel } from './histogram-label';
import { HistogramHtmlLabel } from './histogram-html-label';
import { TRANSLATOR } from './histogram-translator';

const DEBUG_BOOK_LABEL = true;

export interface HistogramBucket {
  displayFrom: number;
  displayTo: number;
  from: number;
  to: number;
}

// Bind mouse events directly to the plot line SVG element found by className.
// addPlotLine() does not support the `events` property for dynamically added lines,
// so we attach native DOM listeners after the elements render.
// A wider invisible overlay path is cloned from the visible plot line to create
// a larger hover target while keeping the visible line at its original width.
const HOVER_HIT_WIDTH = 10;

declare let $: any;
@Component({
  selector: 'rdo-histogram',
  templateUrl: 'histogram-chart.component.html',
  standalone: false,
})
export class HistogramChartComponent implements OnDestroy, OnChanges {
  public labels: any = {};
  hostElement: ElementRef;
  chartConfig: HighchartsConfiguration = new HighchartsConfiguration();
  formatService: FormatService;
  revUtilChartService: RevenueChartHelperService;
  data: Array<any>;
  private chart: any;
  private plotLineTooltipEl: HTMLElement | null = null;
  @Input() chartWidth: number;
  @Input() name: string;
  @Input() chartId: string;
  // eslint-disable-next-line  @angular-eslint/no-input-rename
  @Input('chart-data')
  set chartData(data: any) {
    if (data) {
      this.data = data;
      this.loadChart(data);
    }
  }
  @Output()
  bucketClick: EventEmitter<HistogramBucket> =
    new EventEmitter<HistogramBucket>();

  constructor(
    element: ElementRef,
    formatService: FormatService,
    revUtilChartService: RevenueChartHelperService,
    private translateService: TranslateService
  ) {
    this.hostElement = element;
    this.formatService = formatService;
    this.revUtilChartService = revUtilChartService;
    this.translateService
      .stream('main.tabs.equipment.product_types.rate_types')
      .subscribe((i18n) => {
        this.updateNamesAndTitles();
      });
  }

  ngOnDestroy(): void {
    this.removePlotLineTooltipEl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['chartWidth'] &&
      !changes['chartWidth'].firstChange &&
      this.chart
    ) {
      this.chart.setSize(changes['chartWidth'].currentValue, null, false);
      this.repositionLabelsOnResize();
    }
  }

  /**
   * Repositions renderer labels (book, floor, max, min) after a chart resize
   * since they were placed at absolute x coordinates based on the original container width.
   */
  private repositionLabelsOnResize(): void {
    if (!this.chart) {
      return;
    }
    const newWidth = this.chart.container.offsetWidth;
    if (this.labels.book?.highChartsLabel) {
      this.labels.book.highChartsLabel.attr({ x: newWidth - 45 });
    }
    if (this.labels.floor?.highChartsLabel) {
      const translatedText = this.labels.floor.getText();
      const textWidth = this.labelWidth(translatedText);
      this.labels.floor.highChartsLabel.attr({ x: newWidth - textWidth });
    }
    if (this.labels.max?.highChartsLabel) {
      this.labels.max.highChartsLabel.attr({ x: newWidth - 15 });
    }
    if (this.labels.min?.highChartsLabel) {
      this.labels.min.highChartsLabel.attr({ x: newWidth - 15 });
    }
    this.positionLabels(this);
  }

  private removePlotLineTooltipEl(): void {
    if (this.plotLineTooltipEl) {
      this.plotLineTooltipEl.remove();
      this.plotLineTooltipEl = null;
    }
  }

  private updateNamesAndTitles() {
    if (this.chart) {
      for (const key in TRANSLATOR) {
        // eslint-disable-next-line no-prototype-builtins
        if (TRANSLATOR.hasOwnProperty(key)) {
          const element = TRANSLATOR[key];
          const property = this.chart.get(element.id);
          if (property) {
            const propertyName = element.getName(this);
            property.update({ title: { text: propertyName } });
            property.update({ name: propertyName });
          }
        }
      }
      for (const key in this.labels) {
        // eslint-disable-next-line no-prototype-builtins
        if (this.labels.hasOwnProperty(key)) {
          const label = this.labels[key];
          label.updateLanguage();
        }
      }
    }
  }

  private loadChart(chartData: Array<any>) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component = this;
    let data = chartData;
    if (!_.some(chartData, (o) => o.FromValue && o.ToValue)) {
      data = [];
    }
    const book = _.max(_.map(data, (o) => o.BookRate));
    const floor = _.max(_.map(data, (o) => o.FloorRate));
    const topQ = _.max(_.map(data, (o) => o.TQRateBench));
    const avg = _.max(_.map(data, (o) => o.AvgRateBench));
    const botQ = _.max(_.map(data, (o) => o.BQRateBench));
    const maxb = _.max(_.map(data, (o) => o.MaxRateBench));
    const minb = _.max(_.map(data, (o) => o.MinRateBench));

    const isBin = (obj, value) => {
      return value >= obj.FromValue && value <= obj.ToValue ? value : null;
    };
    const bookPts = _.map(data, (o) => isBin(o, book));
    const benchmarkedTransactionCounts = _.map(data, (o) =>
      o.CountBenchmarked !== 0 ? o.CountBenchmarked : null
    );
    const unbenchmarkedTransactionCounts = _.map(data, (o) => {
      const diff = o.Count - o.CountBenchmarked;
      return diff !== 0 ? diff : null;
    });
    const options = {
      chart: {
        height: 400,
        width:
          this.chartWidth ||
          this.hostElement.nativeElement.firstElementChild?.clientWidth,
        type: 'column',
        inverted: true,
        style: {
          fontSize: '11px',
        },
        events: {
          render: () => {},
        },
      },
      tooltip: {
        outside: true,
        followPointer: true,
        style: {
          fontSize: '10px',
        },
        useHTML: true,
        hideDelay: 200,
        positioner: function (labelWidth, labelHeight, point) {
          const chart = this.chart;
          const containerRect =
            chart.container.parentElement.getBoundingClientRect();
          const gap = 10;

          // With followPointer: true, point.plotX/plotY reflect the mouse position in chart coords
          //const mousePageX = containerRect.left + chart.plotLeft + point.plotY;
          const mousePageX = chart.plotLeft + point.plotX;
          //const mousePageY = containerRect.top + chart.plotTop + point.plotX;
          const mousePageY = chart.plotTop + point.plotY;

          // Default: show to the right of the mouse
          let x = mousePageX + gap;
          // If tooltip would extend past container right edge, flip to left of mouse
          if (x + labelWidth > containerRect.width) {
            x = Math.min(mousePageX, containerRect.width) - labelWidth - gap;
          }
          // If tooltip would overflow past the y-axis (left edge of plot area), clamp to plot area left
          if (x < chart.plotLeft) {
            x = chart.plotLeft;
          }

          // Vertically center on the mouse
          let y = mousePageY - labelHeight / 2;
          // Clamp vertically to viewport
          if (y < 0) {
            y = 0;
          }
          if (y + labelHeight > window.innerHeight) {
            y = window.innerHeight - labelHeight;
          }

          return { x, y };
        },
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          allowPointSelect: true,
          events: {
            click: (x) => {
              this.raiseBucketClickedEvent(x.point);
            },
          },
          grouping: false,
          // This value was 3 making it 0 seems to fix this bug
          // https://rouseservices.atlassian.net/browse/ANA-8823 but not sure if it has other ramifications.
          // https://api.highcharts.com/highcharts/plotOptions.histogram.minPointLength
          // Override this value if you need to show 0 values in the histogram.
          // Not sure why this was only a problem for one bar of the histogram though?
          minPointLength: 0,
        },
        // https://rouseservices.atlassian.net/browse/RIE-3194 - Added this to fix issue of the first label having 0 opacity
        // for some people. I'm not actually sure that this works or not. I'm unable to reproduce the issue on my machine.
        series: {
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      xAxis: {
        categories: _.map(data, (o) => {
          let from = o.FromValue;
          if (!isNaN(parseInt(from))) {
            from = parseInt(from).toLocaleString();
          }

          let to = o.ToValue;
          if (!isNaN(parseInt(to))) {
            to = parseInt(to).toLocaleString();
          }

          const label = `${from} -  ${to}`;
          o.key = label;
          return label;
        }),
        labels: {
          style: {
            fontSize: '11px',
            // https://rouseservices.atlassian.net/browse/RIE-3194 - Added this to fix issue of the first label having 0 opacity
            // for some people. I'm unable to reproduce the issue on my machine.
            opacity: 1,
          },
        },
        reversed: false,
      },
      yAxis: [
        {
          id: TRANSLATOR.TRANSACTION_COUNT.id,
          allowDecimals: false,
          labels: {
            style: {
              fontSize: '11px',
            },
          },
          title: {
            text: TRANSLATOR.TRANSACTION_COUNT.getName(component),
          },
          gridLineWidth: 0,
          minorGridLineWidth: 0,
        },
        {
          labels: {
            style: {
              fontSize: '11px',
            },
            enabled: false,
          },
          title: {
            text: null,
          },
          gridLineWidth: 0,
          max: 1,
          min: 0,
          minorGridLineWidth: 0,
        },
      ],
      series: [
        {
          id: TRANSLATOR.COMPARED_TRANSACTIONS_COUNT.id,
          type: 'column',
          color: 'rgba(45, 108, 162, 0.4)',
          data: benchmarkedTransactionCounts,
          name: TRANSLATOR.COMPARED_TRANSACTIONS_COUNT.getName(component),
          yAxis: 0,
          borderColor: 'rgba(45, 108, 162, 0.4)',
          borderWidth: 1,
        },
        {
          id: TRANSLATOR.NOT_COMPARED_TRANSACTIONS_COUNT.id,
          type: 'column',
          color: 'rgba(255, 255, 255, 0.0)',
          data: unbenchmarkedTransactionCounts,
          name: TRANSLATOR.NOT_COMPARED_TRANSACTIONS_COUNT.getName(component),
          yAxis: 0,
          borderColor: 'rgba(45, 108, 162, 0.4)',
          borderWidth: 1,
        },
        {
          id: TRANSLATOR.BOOK.id,
          type: 'column',
          color: 'rgba(36, 90, 140, 1.0)',
          data: bookPts,
          name: TRANSLATOR.BOOK.getName(component),
          pointPadding: 0.375,
          yAxis: 1,
          className: 'book-line',
        },
        ...(component.allowFloor(floor, book)
          ? [
              {
                id: TRANSLATOR.FLOOR.id,
                type: 'column',
                color: 'rgba(36, 90, 140, 1.0)',
                data: _.map(data, (o) => isBin(o, floor)),
                name: TRANSLATOR.FLOOR.getName(component),
                pointPadding: 0.375,
                yAxis: 1,
              },
            ]
          : []),
        {
          id: TRANSLATOR.BENCH_TOP_Q.id,
          type: 'column',
          color: 'rgba(92,184,92,1.0)',
          data: _.map(data, (o) => isBin(o, topQ)),
          name: TRANSLATOR.BENCH_TOP_Q.getName(component),
          pointPadding: 0.5,
          yAxis: 1,
        },
        {
          id: TRANSLATOR.BENCH_AVG.id,
          type: 'column',
          color: 'rgba(211,211,211,1.0)',
          data: _.map(data, (o) => isBin(o, avg)),
          name: TRANSLATOR.BENCH_AVG.getName(component),
          pointPadding: 0.5,
          yAxis: 1,
        },
        {
          id: TRANSLATOR.BENCH_BOT_Q.id,
          type: 'column',
          color: 'rgba(217,83,79,1.0)',
          data: _.map(data, (o) => isBin(o, botQ)),
          name: TRANSLATOR.BENCH_BOT_Q.getName(component),
          pointPadding: 0.5,
          yAxis: 1,
        },
      ],
    };
    // TODO: Write test
    const getPointIndex = (points, benchmarkValue) => {
      const matchedPoint = _.find(points, (point: any) => {
        const bucket = _.find(data, (b) => b.key === point.category);
        const fromValue = bucket.FromValue;
        const toValue = bucket.ToValue;
        const isMatch =
          benchmarkValue >= fromValue && benchmarkValue <= toValue;
        return isMatch;
      });
      if (matchedPoint) {
        return matchedPoint.index;
      }
      return false;
    };

    const config = new HighchartsConfiguration();
    config.options = options;
    config.exportingOptions = {
      getFileName: () => {
        return this.name ? this.translateService.instant(this.name) : '';
      },
    };
    config.onLoadCallback = (chart) => {
      this.chart = chart;
      let topQIndex = null;
      let avgIndex = null;
      let botQIndex = null;
      let bookIndex = null;
      let floorIndex = null;
      let maxbIndex = null;
      let minbIndex = null;

      const benchTopQPoints = this.chart.get(TRANSLATOR.BENCH_TOP_Q.id).points;
      topQIndex = getPointIndex(benchTopQPoints, topQ);

      const benchAvgPoints = this.chart.get(TRANSLATOR.BENCH_AVG.id).points;
      avgIndex = getPointIndex(benchAvgPoints, avg);

      const benchBotQPoints = this.chart.get(TRANSLATOR.BENCH_BOT_Q.id).points;
      botQIndex = getPointIndex(benchBotQPoints, botQ);

      const bookPoints = this.chart.get(TRANSLATOR.BOOK.id).points;
      bookIndex = getPointIndex(bookPoints, book);

      if (this.allowFloor(floor, book)) {
        const floorPoints = this.chart.get(TRANSLATOR.FLOOR.id).points;
        floorIndex = getPointIndex(floorPoints, floor);
      }

      const maxbPoints = this.chart.get(
        TRANSLATOR.COMPARED_TRANSACTIONS_COUNT.id
      ).points;
      maxbIndex = getPointIndex(maxbPoints, maxb);

      const minbPoints = this.chart.get(
        TRANSLATOR.COMPARED_TRANSACTIONS_COUNT.id
      ).points;
      minbIndex = getPointIndex(minbPoints, minb);

      chart.xAxis[0].addPlotBand({
        color: '#eaf6ea',
        from: avgIndex,
        to: topQIndex,
        zIndex: 0,
      });

      chart.xAxis[0].addPlotBand({
        color: '#fdf7f7',
        from: botQIndex,
        to: avgIndex,
        zIndex: 0,
      });

      // Helper: find the non-null point in a series to use for tooltip display
      const findSeriesPoint = (seriesId: string) => {
        const series: any = chart.get(seriesId);
        if (!series || !series.points) {
          return null;
        }
        return series.points.find((p: any) => p.y != null) || null;
      };

      // Custom HTML tooltip for plot lines, styled to match the popover-charts
      // used by the + and - (bench max/min) labels.
      // Uses a real DOM element with the same CSS classes for visual consistency.
      // Remove any previously leaked tooltip element from a prior chart load.
      this.removePlotLineTooltipEl();
      const ensurePlotLineTooltipEl = () => {
        if (!this.plotLineTooltipEl) {
          this.plotLineTooltipEl = document.createElement('div');
          this.plotLineTooltipEl.className = 'popover-charts left';
          this.plotLineTooltipEl.setAttribute('role', 'tooltip');
          this.plotLineTooltipEl.innerHTML = `
            <div class="arrow"></div>
            <div class="popover-body"></div>
          `;
          this.plotLineTooltipEl.style.display = 'none';
          this.plotLineTooltipEl.style.position = 'fixed';
          this.plotLineTooltipEl.style.pointerEvents = 'none';
          document.body.appendChild(this.plotLineTooltipEl);
        }
        return this.plotLineTooltipEl;
      };

      const showPlotLineTooltip = (
        e: MouseEvent,
        seriesId: string,
        name: string
      ) => {
        const point = findSeriesPoint(seriesId);
        if (!point) {
          return;
        }
        const el = ensurePlotLineTooltipEl();
        const body = el.querySelector('.popover-body');
        body.textContent = `${name}: ${point.y != null ? point.y.toLocaleString() : ''}`;
        el.style.display = 'block';

        // Measure the tooltip and decide which side to show it on
        // Use the chart container's right edge to constrain (not the viewport)
        const elWidth = el.offsetWidth;
        const elHeight = el.offsetHeight;
        const gap = 10;
        const containerRight = chart.container.getBoundingClientRect().right;
        const spaceOnRight = containerRight - e.clientX - gap;

        if (spaceOnRight >= elWidth) {
          // Show to the right with arrow pointing left
          el.className = 'popover-charts right';
          el.style.left = `${e.clientX + gap}px`;
        } else {
          // Not enough room on right — show to the left with arrow pointing right
          el.className = 'popover-charts left';
          el.style.left = `${e.clientX - elWidth - gap}px`;
        }
        el.style.top = `${e.clientY - elHeight / 2}px`;
      };
      const hidePlotLineTooltip = () => {
        if (this.plotLineTooltipEl) {
          this.plotLineTooltipEl.style.display = 'none';
        }
      };

      const bindPlotLineTooltip = (
        className: string,
        seriesId: string,
        displayName: string
      ) => {
        requestAnimationFrame(() => {
          const el = chart.container.querySelector(
            `.${className}`
          ) as SVGElement;
          if (!el) {
            return;
          }

          // Clone the plot line path as a wider invisible overlay for a larger hover area.
          // Append to the top-level SVG so it renders above all series regardless of plot line zIndex.
          const hitArea = el.cloneNode(true) as SVGElement;
          hitArea.setAttribute('stroke', 'transparent');
          hitArea.setAttribute('stroke-width', String(HOVER_HIT_WIDTH));
          hitArea.style.pointerEvents = 'stroke';
          hitArea.style.cursor = 'pointer';
          hitArea.classList.add(`${className}-hit-area`);
          const svg = chart.container.querySelector('.highcharts-root');
          svg.appendChild(hitArea);

          hitArea.addEventListener('mouseover', (e: MouseEvent) => {
            showPlotLineTooltip(e, seriesId, displayName);
          });
          hitArea.addEventListener('mousemove', (e: MouseEvent) => {
            showPlotLineTooltip(e, seriesId, displayName);
          });
          hitArea.addEventListener('mouseout', () => {
            hidePlotLineTooltip();
          });
        });
      };

      // add plot lines (zIndex 2 renders behind series columns; the invisible hit-area clones handle hover)
      // green line
      if (topQIndex !== false) {
        chart.xAxis[0].addPlotLine({
          value: topQIndex,
          color: 'rgba(92,184,92,1.0)',
          width: 3,
          zIndex: 2,
          className: 'bench-top-q-plot-line',
        });
        bindPlotLineTooltip(
          'bench-top-q-plot-line',
          TRANSLATOR.BENCH_TOP_Q.id,
          TRANSLATOR.BENCH_TOP_Q.getName(component)
        );
      }

      if (avgIndex !== false) {
        chart.xAxis[0].addPlotLine({
          value: avgIndex,
          color: 'rgba(211,211,211,1.0)',
          width: 3,
          zIndex: 2,
          className: 'bench-avg-plot-line',
        });
        bindPlotLineTooltip(
          'bench-avg-plot-line',
          TRANSLATOR.BENCH_AVG.id,
          TRANSLATOR.BENCH_AVG.getName(component)
        );
      }

      // red line
      if (botQIndex !== false) {
        chart.xAxis[0].addPlotLine({
          value: botQIndex,
          color: 'rgba(242,108,104,1.0)',
          width: 3,
          zIndex: 2,
          className: 'bench-bot-q-plot-line',
        });
        bindPlotLineTooltip(
          'bench-bot-q-plot-line',
          TRANSLATOR.BENCH_BOT_Q.id,
          TRANSLATOR.BENCH_BOT_Q.getName(component)
        );
      }

      if (data.length > 0) {
        const yPositions = [
          304, 281, 260, 238, 217, 196, 172, 151, 131, 110, 89, 67, 45, 23, 0,
        ]; // offsets for buckets in histogram
        if (book) {
          // Create the book label element.
          const labelX = chart.container.offsetWidth - 45;
          const bookYOffset = -4;
          const labelY = yPositions[bookIndex] + bookYOffset;
          const labelLineColor = 'rgba(36, 90, 140, 1.0)';
          this.labels.book = new HistogramLabel('book', this.translateService);
          this.labels.book.highChartsLabel = chart.renderer
            .label(this.labels.book.getText(), labelX, labelY)
            .css({
              color: 'rgba(36, 90, 140, 1)',
              fontSize: '11px',
              fontWeight: 'bold',
            })
            .attr({
              zIndex: 6,
              class: 'label-position book-label',
            })
            .add();
          chart.xAxis[0].addPlotLine({
            className: 'plot-line-position book-plot-line',
            value: bookIndex,
            color: labelLineColor,
            width: 4,
            zIndex: 2,
          });
          bindPlotLineTooltip(
            'book-plot-line',
            TRANSLATOR.BOOK.id,
            TRANSLATOR.BOOK.getName(component)
          );
        }
        if (this.allowFloor(floor, book)) {
          this.labels.floor = new HistogramLabel(
            'floor',
            this.translateService
          );
          const translatedText = this.labels.floor.getText();
          const textWidth = this.labelWidth(translatedText);
          this.labels.floor.highChartsLabel = chart.renderer
            .label(
              translatedText,
              chart.container.offsetWidth - textWidth,
              yPositions[floorIndex] + 20
            )
            .css({
              color: 'rgba(36, 90, 140, 1)',
              fontSize: '11px',
              fontWeight: 'bold',
            })
            .attr({
              zIndex: 6,
              class: 'label-position floor-label',
            })
            .add();
          chart.xAxis[0].addPlotLine({
            className: 'plot-line-position floor-plot-line',
            value: floorIndex,
            color: 'rgba(36, 90, 140, 1.0)',
            width: 4,
            zIndex: 2,
          });
          bindPlotLineTooltip(
            'floor-plot-line',
            TRANSLATOR.FLOOR.id,
            TRANSLATOR.FLOOR.getName(component)
          );
        }
        if (maxb) {
          const value =
            maxb < 1000 ? maxb : this.formatService.formatNumber(maxb, 0);
          const ypos =
            maxbIndex > 0
              ? yPositions[maxbIndex - 1]
              : yPositions[maxbIndex] + 22;
          this.labels.max = new HistogramHtmlLabel(
            'fa-plus',
            '#36826A',
            value,
            'bench-max',
            'bench_max',
            this.translateService
          );
          const label = chart.renderer
            .label(
              this.labels.max.getHTML(),
              chart.container.offsetWidth - 15,
              ypos,
              null,
              null,
              null,
              true
            )
            .add();
          const popoverText = this.labels.max.getPopOverText();
          $('.bench-max').popover({
            animation: true,
            content: popoverText,
            trigger: 'hover',
            template: `
                <div class="popover-charts left" role="tooltip">
				    <div class="arrow"></div>
					<div class="popover-body"></div>
				</div>
            `,
            html: true,
            placement: 'left',
          });
          this.labels.max.highChartsLabel = label;
        }
        if (minb) {
          const value =
            minb < 1000 ? minb : this.formatService.formatNumber(minb, 0);
          const ypos =
            minbIndex > 0
              ? yPositions[minbIndex - 1]
              : yPositions[minbIndex] + 22;
          this.labels.min = new HistogramHtmlLabel(
            'fa-minus',
            '#A76161',
            value,
            'bench-min',
            'bench_min',
            this.translateService
          );
          const label = chart.renderer
            .label(
              this.labels.min.getHTML(),
              chart.container.offsetWidth - 15,
              ypos,
              null,
              null,
              null,
              true
            )
            .add();
          const popoverText = this.labels.min.getPopOverText();
          $('.bench-min').popover({
            animation: true,
            content: popoverText,
            trigger: 'hover',
            template: `
                <div class="popover-charts left" role="tooltip">
                    <div class="arrow"></div>
				    <div class="popover-body"></div>
			    </div>
            `,
            html: true,
            placement: 'left',
          });
          this.labels.min.highChartsLabel = label;
        }
      }
      this.positionLabels(this);
    };
    this.chartConfig = config;
  }

  private allowFloor(floor: number, book: number) {
    return floor && (floor < book || !book);
  }

  private labelWidth(text: string): number {
    let width = this.formatService.getTextWidth(
      text,
      'bold 11px sans-serif',
      13
    );
    width = width < 25 ? width + 10 : width;
    return width;
  }

  /**
   * https://rouseservices.atlassian.net/browse/RIE-3208
   * Positions the book label based on the plot line position to fix inconsistent placement by the Highcharts library.
   */
  private positionLabels(config: any): void {
    const position = (id: string, type: 'book' | 'floor') => {
      // Get the book label element and its bounding rectangle
      const bookEl = document.querySelector(`#${id} .${type}-label`);
      if (!bookEl) {
        return;
      }
      const margin = 8;
      // Get the container SVG element and its bounding rectangle
      const svg: SVGElement = document.querySelector(`#${id} .highcharts-root`);
      const svgRect = svg.getBoundingClientRect();
      const bookElRect = bookEl.getBoundingClientRect();
      // Get the associated plot line element and its bounding rectangle
      const plotLineEl = document.querySelector(`#${id} .${type}-plot-line`);
      const lineRect = plotLineEl?.getBoundingClientRect() || null;
      // Calculate the correct offset based on the plot line position with a margin and the previous applied transform
      const yOffset = Math.round(
        lineRect.top - svgRect.top - (bookElRect.height + margin)
      );
      const transform = bookEl.getAttribute('transform');
      const parts = transform.split(',');
      const x = parts[0].split('(')[1];
      const newTransform = `translate(${x}, ${yOffset})`;
      bookEl.setAttribute('transform', newTransform);
    };

    if (config.labels.book || config.labels.floor) {
      // The request ensures the elements are in the DOM. Without it they can be null.
      requestAnimationFrame(() => {
        if (config.labels.book) {
          position(this.chartId, 'book');
        }
        if (config.labels.floor) {
          position(this.chartId, 'floor');
        }
      });
    }
  }

  private raiseBucketClickedEvent(point: any) {
    const category = <string>point.category;
    const bucketIndex = _.findIndex(this.data, (i) => i.key === category);
    const bucket = this.data[bucketIndex];

    const displayFromValue: number = bucket.FromValue;
    const displayToValue: number = bucket.ToValue;
    let fromValue: number = bucket.FromValue;
    let toValue: number = bucket.ToValue;

    // don't pass from/to value for top/bottom bucket
    // to avoid stored procedure bin range rounding issues
    if (bucketIndex === 0) {
      fromValue = null;
    } else if (bucketIndex === this.data.length - 1) {
      toValue = null;
    }

    this.bucketClick.next(<HistogramBucket>{
      displayFrom: displayFromValue,
      displayTo: displayToValue,
      from: fromValue == null ? null : fromValue - 0.5,
      to: toValue == null ? null : toValue + 0.5,
    });
  }
}
